import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
} from "@mui/material";
import { toastMessage } from "shared/components/toast";
import { generateBulkQRCodeService } from "shared/services/qrCodeService";
import Loader from "shared/components/loader/animation";

interface GenerateBulkQRModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function GenerateBulkQRModal({ open, onClose, onSuccess }: GenerateBulkQRModalProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<{
    event_name: string;
    plan_type: number;
    duration: number | string;
    coins: number | string;
    price: number | string;
    quantity: number | string;
    status: number;
    expires_at: string;
  }>({
    event_name: "",
    plan_type: 1,
    duration: 90,
    coins: 1,
    price: 0,
    quantity: 1,
    status: 1,
    expires_at: "",
  });

  const [errors, setErrors] = useState<any>({});

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev: any) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.event_name || formData.event_name.trim() === "") {
      newErrors.event_name = "Event name is required";
    } else if (formData.event_name.length > 255) {
      newErrors.event_name = "Event name must not exceed 255 characters";
    }

    const duration = typeof formData.duration === 'string' ? parseInt(formData.duration) : formData.duration;
    const coins = typeof formData.coins === 'string' ? parseInt(formData.coins) : formData.coins;
    const price = typeof formData.price === 'string' ? parseFloat(formData.price) : formData.price;
    const quantity = typeof formData.quantity === 'string' ? parseInt(formData.quantity) : formData.quantity;

    if (!duration || isNaN(duration) || duration < 1 || duration > 365) {
      newErrors.duration = "Duration must be between 1 and 365 days";
    }

    if (!coins || isNaN(coins) || coins <= 0) {
      newErrors.coins = "Coins must be greater than 0";
    }

    if (isNaN(price) || price < 0) {
      newErrors.price = "Price must be greater than or equal to 0";
    }

    if (!quantity || isNaN(quantity) || quantity < 1 || quantity > 1000) {
      newErrors.quantity = "Quantity must be between 1 and 1000";
    }

    if (formData.expires_at) {
      const expiryDate = new Date(formData.expires_at);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (expiryDate <= today) {
        newErrors.expires_at = "Expiry date must be after today";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    const payload: any = {
      event_name: formData.event_name.trim(),
      plan_type: formData.plan_type,
      duration: typeof formData.duration === 'string' ? parseInt(formData.duration) : formData.duration,
      coins: typeof formData.coins === 'string' ? parseInt(formData.coins) : formData.coins,
      price: typeof formData.price === 'string' ? parseFloat(formData.price) : formData.price,
      quantity: typeof formData.quantity === 'string' ? parseInt(formData.quantity) : formData.quantity,
      status: formData.status,
    };

    if (formData.expires_at) {
      payload.expires_at = formData.expires_at;
    }

    generateBulkQRCodeService(payload)
      .then(({ data }) => {
        if (data?.success || data?.status) {
          toastMessage("success", data?.message || "QR codes generated successfully");
          handleClose();
          onSuccess();
        }
        setLoading(false);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message || "Failed to generate QR codes");
        setLoading(false);
      });
  };

  const handleClose = () => {
    setFormData({
      event_name: "",
      plan_type: 1,
      duration: 90,
      coins: 1,
      price: 0,
      quantity: 1,
      status: 1,
      expires_at: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate Bulk QR Codes</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Event Name"
              value={formData.event_name}
              onChange={(e) => handleChange("event_name", e.target.value)}
              error={!!errors.event_name}
              helperText={errors.event_name || "Name for grouping QR codes"}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Plan Type</InputLabel>
              <Select
                value={formData.plan_type}
                label="Plan Type"
                onChange={(e) => handleChange("plan_type", e.target.value)}
              >
                <MenuItem value={1}>Individual</MenuItem>
                <MenuItem value={2}>Family</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => handleChange("quantity", e.target.value === "" ? "" : parseInt(e.target.value))}
              error={!!errors.quantity}
              helperText={errors.quantity}
              inputProps={{ min: 1, max: 1000 }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Duration (days)"
              type="number"
              value={formData.duration}
              onChange={(e) => handleChange("duration", e.target.value === "" ? "" : parseInt(e.target.value))}
              error={!!errors.duration}
              helperText={errors.duration}
              inputProps={{ min: 1, max: 365 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Coins"
              type="number"
              value={formData.coins}
              onChange={(e) => handleChange("coins", e.target.value === "" ? "" : parseInt(e.target.value))}
              error={!!errors.coins}
              helperText={errors.coins}
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Price"
              type="number"
              value={formData.price}
              onChange={(e) => handleChange("price", e.target.value === "" ? "" : parseFloat(e.target.value))}
              error={!!errors.price}
              helperText={errors.price}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleChange("status", e.target.value)}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Expiry Date (Optional)"
              type="date"
              value={formData.expires_at}
              onChange={(e) => handleChange("expires_at", e.target.value)}
              error={!!errors.expires_at}
              helperText={errors.expires_at || "Leave empty for no expiry"}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? "Generating..." : `Generate ${formData.quantity} QR Codes`}
        </Button>
      </DialogActions>
      {loading && <Loader />}
    </Dialog>
  );
}

export default GenerateBulkQRModal;
